import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import CustomDrawer from "ui/Drawer"
import EquipmentFilters from "../EquipmentFilters"
import { CustomSelect } from "ui/Input/Select"
import { CALENDAR_TABS } from "../../constants"
import styles from "../../Calendar.module.css"

const renderFilterTag = ({ label, closable, onClose }, styles) => (
  <span className={styles.filterTag}>
    <span className={styles.filterTagLabel}>{label}</span>
    {closable && (
      <button type="button" className={styles.filterTagRemove} onClick={onClose} aria-label="Remove">
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    )}
  </span>
)

const MobileFiltersDrawer = ({ open, setOpen, activeTab, params, filters, crewFilters, setFilters, setCrewFilters, experienceOptions, crewMemberOptionsWithUnassigned, equipmentOptions, roles, calendarFilterSelectProps, setEquipmentCategoryOptions }) => {
  const { t } = useTranslation()
  const tagRenderer = (props) => renderFilterTag(props, styles)

  const handleClear = () => {
    if (activeTab === CALENDAR_TABS.CREW) {
      setCrewFilters({ roles: [], crewMembers: [] })
    } else if (activeTab === CALENDAR_TABS.EQUIPMENT) {
      setFilters((prev) => ({ ...prev, equipmentFilters: { equipment: [], category: [] } }))
    } else {
      setFilters({ experience: [], crew: [], equipment: [] })
    }
    setOpen(false)
  }

  const renderContent = () => {
    if (activeTab === CALENDAR_TABS.CREW) {
      return (
        <>
          <CustomSelect mode="multiple" style={{ width: "100%" }} placeholder={crewFilters.roles.length > 0 ? `Roles (${crewFilters.roles.length})` : "Roles"} className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`} {...calendarFilterSelectProps} value={crewFilters.roles} onChange={(v) => setCrewFilters((prev) => ({ ...prev, roles: v }))} options={Array.isArray(roles) ? roles.map((r) => ({ label: r.name, value: r.id })) : []} maxTagCount="responsive" allowClear tagRender={tagRenderer} />
          <CustomSelect mode="multiple" placeholder={crewFilters.crewMembers.length > 0 ? `Crew Member (${crewFilters.crewMembers.length})` : "Crew Member"} className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`} style={{ width: "100%" }} {...calendarFilterSelectProps} value={crewFilters.crewMembers} onChange={(v) => setCrewFilters((prev) => ({ ...prev, crewMembers: v }))} options={crewMemberOptionsWithUnassigned} maxTagCount="responsive" allowClear tagRender={tagRenderer} />
        </>
      )
    }
    if (activeTab === CALENDAR_TABS.EQUIPMENT) {
      return <EquipmentFilters value={filters.equipmentFilters || {}} onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))} onOptionsChange={({ categoryOptions }) => setEquipmentCategoryOptions(categoryOptions || [])} hideSearch fullWidth selectProps={calendarFilterSelectProps} />
    }
    return (
      <>
        {activeTab !== CALENDAR_TABS.MY_BOOKINGS && (
          <CustomSelect mode="multiple" placeholder={t("calendar.filters.crew_member", "Crew Member")} className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`} style={{ width: "100%" }} {...calendarFilterSelectProps} value={filters.crew} onChange={(v) => setFilters((prev) => ({ ...prev, crew: v }))} options={crewMemberOptionsWithUnassigned} maxTagCount="responsive" allowClear tagRender={tagRenderer} />
        )}
        <CustomSelect mode="multiple" placeholder={t("calendar.filters.equipment", "Equipment")} className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`} style={{ width: "100%" }} {...calendarFilterSelectProps} value={filters.equipment} onChange={(v) => setFilters((prev) => ({ ...prev, equipment: v }))} options={equipmentOptions} maxTagCount="responsive" allowClear tagRender={tagRenderer} />
        {params.range === "date" && (
          <CustomSelect mode="multiple" placeholder={t("calendar.filters.experience", "Experience")} className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`} style={{ width: "100%" }} {...calendarFilterSelectProps} value={filters.experience} onChange={(v) => setFilters((prev) => ({ ...prev, experience: v }))} options={experienceOptions} maxTagCount="responsive" allowClear tagRender={tagRenderer} />
        )}
      </>
    )
  }

  return (
    <CustomDrawer open={open} setOpen={setOpen} title={t("calendar.filters.title", "Filters")}>
      <Flex vertical gap={10}>
        {renderContent()}
        <Flex gap={12}>
          <Button size="large" className="flex-1" type="primary" onClick={() => setOpen(false)}>{t("calendar.filters.apply", "Apply")}</Button>
          <Button size="large" className="flex-1" color="primary" variant="outlined" onClick={handleClear}>{t("calendar.filters.clear", "Clear Filters")}</Button>
        </Flex>
      </Flex>
    </CustomDrawer>
  )
}

MobileFiltersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  crewFilters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setCrewFilters: PropTypes.func.isRequired,
  experienceOptions: PropTypes.array.isRequired,
  crewMemberOptionsWithUnassigned: PropTypes.array.isRequired,
  equipmentOptions: PropTypes.array.isRequired,
  roles: PropTypes.array,
  calendarFilterSelectProps: PropTypes.object,
  setEquipmentCategoryOptions: PropTypes.func.isRequired,
}

export default MobileFiltersDrawer
